import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import MarkdownHelp from '../ui/MarkdownHelp';

interface FollowUpReminderPopupProps {
  contactId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function FollowUpReminderPopup({ contactId, onClose, onSuccess }: FollowUpReminderPopupProps) {
  const [dueDate, setDueDate] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { error } = await supabase
        .from('nrm_follow_up_reminders')
        .insert([{
          contact_id: contactId,
          due_date: new Date(dueDate + 'T00:00:00Z'),
          description
        }]);

      if (error) throw error;

      // Also create a timeline entry for this reminder
      await supabase
        .from('nrm_timeline_entries')
        .insert([{
          contact_id: contactId,
          date: new Date(),
          content: `Set follow-up reminder for ${new Date(dueDate).toLocaleDateString()}: ${description}`
        }]);

      onSuccess();
    } catch (err) {
      console.error('Failed to save follow-up reminder:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-dark-800 rounded-lg w-[60%] h-[80%] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Add Follow Up Reminder</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-[calc(100%-4rem)]">
          <div className="flex-none space-y-4">
            <div>
              <label htmlFor="due_date" className="block text-sm font-medium text-gray-300 mb-1">
                Due Date
              </label>
              <input
                type="date"
                id="due_date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="input w-full"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
                Description (Markdown supported)
                <div className="mt-1">
                  <MarkdownHelp />
                </div>
              </label>
            </div>
          </div>

          <div className="flex-grow flex flex-col min-h-0 my-4">
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full flex-grow"
              placeholder="Enter your follow-up notes here..."
              required
            />
          </div>

          <div className="flex-none flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}